import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { getVisitorsList } from '../../../../redux/actions/SocietyPortal/VisitorAction/VisitorAction';
import FormSection from '../components/form/FormSection';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import { labelCls, errorCls, hintCls, controlCls, numberCls } from '../components/form/formStyles';
import {
    VEHICLE_TYPES,
    WEEKDAYS,
    toOptions,
    toDateInput,
} from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const VEHICLE_OPTIONS = toOptions(VEHICLE_TYPES);

// The three types are labels only — the backend validates all of them
// identically. Saying so keeps anyone from expecting PERMANENT to skip the
// date window.
const TYPE_OPTIONS = [
    { value: 'TEMPORARY', label: 'Temporary' },
    { value: 'RECURRING', label: 'Recurring' },
    { value: 'PERMANENT', label: 'Permanent' },
];

const EMPTY = {
    visitor_id: '',
    wing_id: '',
    flat_id: '',
    pass_type: 'TEMPORARY',
    purpose: '',
    valid_from: '',
    valid_until: '',
    allowed_days: [],
    allowed_time_from: '',
    allowed_time_to: '',
    max_entries: '',
    vehicle_number: '',
    vehicle_type: '',
    remarks: '',
};

// Create/edit form for a gate pass — a standing authorisation for a visitor who
// already exists, unlike a pre-approval which matches a raw phone number.
const GatePassForm = ({
    societyId,
    initial,
    mode = 'create',
    submitting = false,
    onSubmit,
    onCancel,
}) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
    const [errors, setErrors] = useState({});

    const { visitorsListData, visitorsListLoading } = useSelector(state => state.VisitorReducer) || {};

    // A pass is keyed on visitor_id, so the picker needs the society's existing
    // visitors. The limit is deliberately high — this is a picker, not a page.
    useEffect(() => {
        if (mode === 'create' && societyId) {
            dispatch(getVisitorsList({ societyId, page: 1, limit: 100, isBlocked: 'false' }));
        }
    }, [dispatch, mode, societyId]);

    useEffect(() => {
        if (initial) setForm(f => ({ ...f, ...initial }));
    }, [initial]);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const visitorOptions = (Array.isArray(visitorsListData) ? visitorsListData : []).map(v => ({
        value: String(v.visitor_id),
        label: `${v.name} · ${fmtPhone(v.phone)}`,
    }));

    const toggleDay = (day) => {
        setForm(f => {
            const current = Array.isArray(f.allowed_days) ? f.allowed_days : [];
            return {
                ...f,
                allowed_days: current.includes(day) ? current.filter(d => d !== day) : [...current, day],
            };
        });
    };

    const validate = () => {
        const e = {};
        if (mode === 'create') {
            if (!form.visitor_id) e.visitor_id = 'Pick the visitor this pass is for';
            if (!form.wing_id) e.wing_id = 'Wing is required';
            if (!form.flat_id) e.flat_id = 'Flat is required';
        }
        if (!form.valid_from) e.valid_from = 'Start date is required';
        if (!form.valid_until) e.valid_until = 'End date is required';
        // The API accepts an inverted window and produces a pass that never
        // matches, so this check is the only thing preventing a dead record.
        if (form.valid_from && form.valid_until && new Date(form.valid_until) < new Date(form.valid_from)) {
            e.valid_until = 'Must be on or after the start date';
        }
        // A time window is only applied when both ends are set — one alone is
        // silently ignored by the validator, which looks like a working
        // restriction but is not.
        if (!!form.allowed_time_from !== !!form.allowed_time_to) {
            e.allowed_time_to = 'Set both times, or neither — one alone is ignored at the gate';
        }
        if (form.allowed_time_from && form.allowed_time_to && form.allowed_time_to <= form.allowed_time_from) {
            e.allowed_time_to = 'Must be after the start time';
        }
        if (form.max_entries !== '' && Number(form.max_entries) < 1) {
            e.max_entries = 'Must be at least 1, or leave blank for unlimited';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;

        const payload = {
            pass_type: form.pass_type,
            // Stored as DATEONLY — any time component is discarded server-side.
            valid_from: toDateInput(form.valid_from),
            valid_until: toDateInput(form.valid_until),
        };
        if (form.purpose.trim()) payload.purpose = form.purpose.trim();
        if (form.remarks.trim()) payload.remarks = form.remarks.trim();
        if (form.vehicle_number.trim()) payload.vehicle_number = form.vehicle_number.trim();
        if (form.vehicle_type) payload.vehicle_type = form.vehicle_type;
        // An empty array means "every day" to the validator, same as null.
        if (form.allowed_days.length > 0) payload.allowed_days = form.allowed_days;
        if (form.allowed_time_from && form.allowed_time_to) {
            payload.allowed_time_from = form.allowed_time_from;
            payload.allowed_time_to = form.allowed_time_to;
        }
        if (form.max_entries !== '') payload.max_entries = Number(form.max_entries);

        if (mode === 'create') {
            payload.society_id = Number(societyId);
            payload.visitor_id = Number(form.visitor_id);
            payload.wing_id = Number(form.wing_id);
            payload.flat_id = Number(form.flat_id);
        }

        onSubmit(payload);
    };

    const unwrap = (v) => (v === 'all' ? '' : v);
    const everyDay = !form.allowed_days || form.allowed_days.length === 0;

    return (
        <form onSubmit={handleSubmit} className="tw-space-y-5">
            <FormSection
                icon="mdi:account-check-outline"
                title="Who and where"
                hint="A gate pass is issued to an existing visitor — register them first if they are new"
            >
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                    {mode === 'create' ? (
                        <>
                            <div>
                                <label className={labelCls}>Visitor <span className="tw-text-rose-500">*</span></label>
                                <SearchableDropdown
                                    small
                                    clearable={false}
                                    loading={!!visitorsListLoading}
                                    value={form.visitor_id}
                                    onChange={(v) => set('visitor_id', v)}
                                    options={visitorOptions}
                                    placeholder="Search visitors"
                                />
                                {errors.visitor_id
                                    ? <p className={errorCls}>{errors.visitor_id}</p>
                                    : <p className={hintCls}>Blocked visitors are not listed</p>}
                            </div>
                            <WingFlatPicker
                                societyId={societyId}
                                wingId={form.wing_id}
                                flatId={form.flat_id}
                                onWingChange={(v) => set('wing_id', v)}
                                onFlatChange={(v) => set('flat_id', v)}
                                errors={errors}
                            />
                        </>
                    ) : (
                        <div className="sm:tw-col-span-3">
                            <label className={labelCls}>Issued to</label>
                            <p className="tw-mb-0 tw-px-3 tw-py-2 tw-text-xs tw-text-gray-600 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-md">
                                {initial?.visitorLabel || '—'}
                                <span className="tw-text-gray-400"> · visitor and flat are fixed after issue</span>
                            </p>
                        </div>
                    )}
                </div>

                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-mt-3">
                    <div>
                        <label className={labelCls}>Pass type</label>
                        <SearchableDropdown
                            small
                            clearable={false}
                            value={form.pass_type}
                            onChange={(v) => set('pass_type', v)}
                            options={TYPE_OPTIONS}
                            placeholder="Select type"
                        />
                        <p className={hintCls}>A label only — all three follow the same rules below</p>
                    </div>
                    <div className="sm:tw-col-span-2">
                        <label className={labelCls}>Purpose</label>
                        <input
                            type="text"
                            value={form.purpose}
                            onChange={e => set('purpose', e.target.value)}
                            maxLength={255}
                            placeholder="e.g. Daily housekeeping"
                            className={controlCls(false)}
                        />
                    </div>
                </div>
            </FormSection>

            <FormSection
                icon="mdi:calendar-range"
                title="When the pass works"
                hint="Every rule here is checked at the gate — leave one blank to place no limit"
            >
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-3">
                    <div>
                        <label className={labelCls}>Valid from <span className="tw-text-rose-500">*</span></label>
                        <input
                            type="date"
                            value={form.valid_from}
                            onChange={e => set('valid_from', e.target.value)}
                            className={controlCls(!!errors.valid_from)}
                        />
                        {errors.valid_from && <p className={errorCls}>{errors.valid_from}</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Valid until <span className="tw-text-rose-500">*</span></label>
                        <input
                            type="date"
                            value={form.valid_until}
                            onChange={e => set('valid_until', e.target.value)}
                            className={controlCls(!!errors.valid_until)}
                        />
                        {errors.valid_until && <p className={errorCls}>{errors.valid_until}</p>}
                    </div>
                    <div>
                        <label className={labelCls}>From time</label>
                        <input
                            type="time"
                            value={form.allowed_time_from}
                            onChange={e => set('allowed_time_from', e.target.value)}
                            className={controlCls(false)}
                        />
                    </div>
                    <div>
                        <label className={labelCls}>To time</label>
                        <input
                            type="time"
                            value={form.allowed_time_to}
                            onChange={e => set('allowed_time_to', e.target.value)}
                            className={controlCls(!!errors.allowed_time_to)}
                        />
                        {errors.allowed_time_to
                            ? <p className={errorCls}>{errors.allowed_time_to}</p>
                            : <p className={hintCls}>Both or neither</p>}
                    </div>
                </div>

                <div className="tw-mt-3">
                    <label className={labelCls}>
                        Allowed days
                        <span className="tw-font-normal tw-text-gray-400"> — none selected means every day</span>
                    </label>
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                        {WEEKDAYS.map(d => {
                            const active = (form.allowed_days || []).includes(d);
                            return (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => toggleDay(d)}
                                    className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-semibold tw-border tw-transition-colors ${active
                                        ? 'tw-bg-sky-600 tw-text-white tw-border-sky-600'
                                        : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'
                                        }`}
                                >
                                    {d}
                                </button>
                            );
                        })}
                        {!everyDay && (
                            <button
                                type="button"
                                onClick={() => set('allowed_days', [])}
                                className="tw-px-3 tw-py-1 tw-rounded-full tw-text-[10px] tw-font-medium tw-text-gray-500 hover:tw-text-gray-700"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {everyDay && <p className={hintCls}>Valid on every day of the week</p>}
                </div>

                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-mt-3">
                    <div>
                        <label className={labelCls}>Maximum entries</label>
                        <input
                            type="number"
                            min={1}
                            value={form.max_entries}
                            onChange={e => set('max_entries', e.target.value)}
                            placeholder="Unlimited"
                            className={numberCls(!!errors.max_entries)}
                        />
                        {errors.max_entries
                            ? <p className={errorCls}>{errors.max_entries}</p>
                            : <p className={hintCls}>Blank for unlimited</p>}
                    </div>
                    <div>
                        <label className={labelCls}>Vehicle number</label>
                        <input
                            type="text"
                            value={form.vehicle_number}
                            onChange={e => set('vehicle_number', e.target.value.toUpperCase())}
                            maxLength={20}
                            placeholder="optional"
                            className={`${controlCls(false)} tw-font-mono`}
                        />
                    </div>
                    <div>
                        <label className={labelCls}>Vehicle type</label>
                        <SearchableDropdown
                            small
                            value={form.vehicle_type}
                            onChange={(v) => set('vehicle_type', unwrap(v))}
                            options={VEHICLE_OPTIONS}
                            placeholder="None"
                        />
                    </div>
                </div>

                <div className="tw-mt-3">
                    <label className={labelCls}>Remarks</label>
                    <textarea
                        rows={2}
                        value={form.remarks}
                        onChange={e => set('remarks', e.target.value)}
                        maxLength={1000}
                        placeholder="Anything the gate should know"
                        className={`${controlCls(false)} tw-resize-none`}
                    />
                </div>
            </FormSection>

            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-3 tw-border-t tw-border-gray-100">
                <button
                    type="button"
                    onClick={onCancel}
                    className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                >
                    <Icon icon="mdi:card-account-details-outline" className="tw-text-base" />
                    {submitting ? 'Saving...' : (mode === 'create' ? 'Issue Gate Pass' : 'Save Changes')}
                </button>
            </div>
        </form>
    );
};

export default GatePassForm;
